/**
 * SSE parsing and stream result assembly.
 * Port of fastSDK's `sse_assembly.py`: one shared implementation for decoding
 * server-sent events and for turning a fully drained stream body into a
 * single result value (text, typed media file, or raw bytes).
 */

import { MediaFileFactory, MediaFile } from '@socaity/media-toolkit';

const isNode = typeof window === 'undefined';

/** Extract text from an OpenAI-style SSE chunk, or stringify it. */
export function chunkText(chunk: any): string {
  if (typeof chunk === 'string') return chunk;
  if (chunk && typeof chunk === 'object') {
    const choice = chunk.choices?.[0];
    if (choice) {
      const delta = choice.delta ?? choice.message ?? {};
      return delta.content ?? '';
    }
  }
  return String(chunk);
}

/** Fast base64 shape check without decoding the payload. */
export function looksLikeBase64(payload: string): boolean {
  if (!payload || payload.length % 4 !== 0) return false;
  return /^[A-Za-z0-9+/]+=*$/.test(payload);
}

export function decodeBase64(payload: string): Uint8Array {
  if (isNode) {
    const buffer = Buffer.from(payload, 'base64');
    return new Uint8Array(buffer.buffer, buffer.byteOffset, buffer.byteLength);
  }
  const binary = atob(payload);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i++) bytes[i] = binary.charCodeAt(i);
  return bytes;
}

export function concatBytes(chunks: Uint8Array[]): Uint8Array {
  const total = chunks.reduce((sum, chunk) => sum + chunk.length, 0);
  const out = new Uint8Array(total);
  let offset = 0;
  for (const chunk of chunks) {
    out.set(chunk, offset);
    offset += chunk.length;
  }
  return out;
}

function isMostlyText(data: Uint8Array): boolean {
  if (!data.length || data.includes(0)) return false;
  let text: string;
  try {
    text = new TextDecoder('utf-8', { fatal: true }).decode(data);
  } catch {
    return false;
  }
  let printable = 0;
  for (const char of text) {
    // eslint-disable-next-line no-control-regex
    if (!/[\x00-\x08\x0b\x0c\x0e-\x1f]/.test(char)) printable++;
  }
  return printable / text.length > 0.95;
}

/** Turn raw bytes into text, a typed media file, or the raw byte payload. */
export async function bytesToResult(data: Uint8Array): Promise<string | MediaFile | Uint8Array> {
  if (isMostlyText(data)) return new TextDecoder().decode(data);
  try {
    const media = await MediaFileFactory.create(data);
    const contentType = media.getContentType?.() ?? '';
    if (/^(image|audio|video)\//.test(contentType) || contentType.startsWith('model/')) {
      return media;
    }
    return data;
  } catch {
    return data;
  }
}

/** Parse SSE-framed bytes; returns null when the body is raw passthrough. */
async function assembleSseBytes(data: Uint8Array): Promise<any | null> {
  if (!data.length) return '';

  const jsonPayloads: string[] = [];
  const binaryChunks: Uint8Array[] = [];
  const textPayloads: string[] = [];
  let sawDataLine = false;

  for (const rawLine of new TextDecoder().decode(data).split(/\r?\n/)) {
    const line = rawLine.trim();
    if (!line || line.startsWith(':')) continue;
    if (!line.startsWith('data:')) {
      if (sawDataLine) continue;
      return null;
    }
    sawDataLine = true;
    const payload = line.slice(5).trim();
    if (payload === '[DONE]') continue;
    if (payload.startsWith('{')) {
      jsonPayloads.push(payload);
    } else if (looksLikeBase64(payload)) {
      try {
        binaryChunks.push(decodeBase64(payload));
      } catch {
        textPayloads.push(payload);
      }
    } else {
      textPayloads.push(payload);
    }
  }

  if (jsonPayloads.length) {
    return jsonPayloads.map((payload) => chunkText(JSON.parse(payload))).join('');
  }
  if (binaryChunks.length) {
    return bytesToResult(concatBytes(binaryChunks));
  }
  if (textPayloads.length) {
    return textPayloads.join('');
  }
  return '';
}

/** Assemble a fully drained streaming response body into one result value. */
export async function assembleStreamBytes(data: Uint8Array, isSse: boolean): Promise<any> {
  if (isSse) {
    const parsed = await assembleSseBytes(data);
    if (parsed !== null) return parsed;
  }
  return bytesToResult(data);
}
